import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, FileText, Calculator as CalcIcon, TrendingUp } from "lucide-react";
import { getStats, listQuotes, formatUSD } from "@/lib/api";

const StatTile = ({ label, value, sub, testid }) => (
  <div className="border border-[#050505] p-6 lg:p-8 bg-white" data-testid={testid}>
    <div className="text-label mb-6">{label}</div>
    <div className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none">
      {value}
    </div>
    {sub && <div className="mt-4 text-xs uppercase tracking-[0.18em] text-[#525252]">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getStats(), listQuotes()])
      .then(([s, q]) => {
        setStats(s);
        setQuotes(q.slice(0, 5));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="dashboard-page">
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#050505] mb-12">
        <div className="lg:col-span-8 p-8 lg:p-14 border-b lg:border-b-0 lg:border-r border-[#050505]">
          <div className="text-label mb-6" data-testid="hero-tag">№ 001 — Quoting Studio</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
            Price any book.<br />
            <span className="text-[#FF3333]">Down to the page.</span>
          </h1>
          <p className="mt-8 max-w-xl text-base text-[#525252] leading-relaxed">
            Trim size, paper weight, color mode, binding, finishing, quantity — every
            variable. One clean breakdown. Save quotes, send them out, win the job.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/calculator" className="btn-primary" data-testid="hero-cta-calculator">
              <CalcIcon className="w-4 h-4" strokeWidth={1.5} />
              Build a Quote
            </Link>
            <Link to="/quotes" className="btn-secondary" data-testid="hero-cta-history">
              <FileText className="w-4 h-4" strokeWidth={1.5} />
              View All Quotes
            </Link>
          </div>
        </div>
        <div className="lg:col-span-4 p-8 lg:p-14 bg-[#F5F5F5] flex flex-col justify-between">
          <div className="text-label">Spec inputs</div>
          <ul className="mt-6 space-y-3 font-mono text-sm">
            {[
              "TRIM SIZE",
              "PAGE COUNT",
              "INTERIOR COLOR",
              "PAPER + WEIGHT",
              "COVER TYPE",
              "BINDING",
              "FINISHING",
              "QUANTITY",
            ].map((x, i) => (
              <li key={x} className="flex items-baseline justify-between border-b border-[#e5e5e5] pb-2">
                <span className="text-[#050505]">{x}</span>
                <span className="text-[#525252]">{String(i + 1).padStart(2, "0")}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 text-[11px] uppercase tracking-[0.2em] text-[#525252]">
            Live breakdown · USD
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 mb-12 -mx-px">
        <StatTile
          testid="stat-total-quotes"
          label="Total Quotes"
          value={loading ? "—" : (stats?.total_quotes ?? 0)}
          sub="all time"
        />
        <StatTile
          testid="stat-avg-value"
          label="Avg Quote"
          value={loading ? "—" : formatUSD(stats?.avg_quote_value || 0)}
          sub="per project"
        />
        <StatTile
          testid="stat-total-value"
          label="Total Value"
          value={loading ? "—" : formatUSD(stats?.total_value || 0)}
          sub="quoted"
        />
        <StatTile
          testid="stat-books-quoted"
          label="Books Quoted"
          value={loading ? "—" : (stats?.total_books_quoted ?? 0).toLocaleString()}
          sub="total copies"
        />
      </section>

      {/* Recent */}
      <section className="border border-[#050505]" data-testid="recent-quotes-section">
        <div className="flex items-center justify-between border-b border-[#050505] px-6 lg:px-8 py-5">
          <h2 className="font-display font-bold text-xl tracking-tight uppercase">
            Recent Activity
          </h2>
          <Link to="/quotes" className="text-xs uppercase tracking-[0.2em] font-bold ghost-link inline-flex items-center gap-1" data-testid="recent-view-all">
            View all <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#525252] font-mono text-sm">Loading…</div>
        ) : quotes.length === 0 ? (
          <div className="p-12 lg:p-16 text-center">
            <BookOpen className="w-10 h-10 mx-auto mb-4 text-[#050505]" strokeWidth={1.5} />
            <div className="font-display text-2xl font-bold mb-2">No quotes yet</div>
            <div className="text-sm text-[#525252] mb-6">Start by building your first book quote.</div>
            <Link to="/calculator" className="btn-primary" data-testid="empty-cta-calculator">
              <CalcIcon className="w-4 h-4" strokeWidth={1.5} /> Build Quote
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#050505]">
                <th className="text-left px-6 lg:px-8 py-4 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Quote</th>
                <th className="text-left px-4 py-4 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Project</th>
                <th className="text-left px-4 py-4 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Client</th>
                <th className="text-left px-4 py-4 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Status</th>
                <th className="text-right px-6 lg:px-8 py-4 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Total</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} className="border-b border-[#e5e5e5] hover:bg-[#f5f5f5] transition-colors" data-testid={`recent-row-${q.id}`}>
                  <td className="px-6 lg:px-8 py-4">
                    <Link to={`/quotes/${q.id}`} className="font-mono text-sm font-medium ghost-link">
                      {q.quote_number}
                    </Link>
                  </td>
                  <td className="px-4 py-4 text-sm">{q.project_title || <span className="text-[#525252]">—</span>}</td>
                  <td className="px-4 py-4 text-sm">{q.client_name || <span className="text-[#525252]">—</span>}</td>
                  <td className="px-4 py-4">
                    <span className={`status-pill status-${q.status}`}>{q.status}</span>
                  </td>
                  <td className="px-6 lg:px-8 py-4 text-right font-mono text-sm font-medium">
                    {formatUSD(q.breakdown?.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Feature row */}
      <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#050505]">
        {[
          { icon: CalcIcon, title: "Live Pricing", body: "Watch the price update as you change every spec. No surprises." },
          { icon: TrendingUp, title: "Volume Discounts", body: "Tiered discounts kick in automatically based on print run." },
          { icon: FileText, title: "Print-Ready Quotes", body: "Export quotes to PDF directly from the browser. Looks like a master print-spec." },
        ].map((f, i) => (
          <div key={f.title} className={`p-8 lg:p-10 ${i < 2 ? "md:border-r border-[#050505]" : ""} ${i === 0 ? "border-b md:border-b-0 border-[#050505]" : i === 1 ? "border-b md:border-b-0 border-[#050505]" : ""}`}>
            <f.icon className="w-6 h-6 mb-6" strokeWidth={1.5} />
            <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
            <p className="text-sm text-[#525252] leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
